import React from 'react';
import { CommandeBC, StatusId, TabDescriptor } from '../types';
import CommandeSuiviMarche from './CommandeSuiviMarche';
import CommandeConsultation from './CommandeConsultation';
import CommandeOffre from './CommandeOffre';
import CommandeNotification from './CommandeNotification';
import CommandeEvaluationTab from '../../../composants/AvisPrestataire/CommandeEvaluationTab';
import TabMessagerie from '../../../composants/TabMessagerie/TabMessagerie';
import NextTabButton from '../../../composants/NextTabButton/NextTabButton';
import { isTabReachable, computeMessagerieNextTabCta } from '../domain/commandeNextTab';
import { MessageChannel } from '../../../services/messagerie/useUnreadCounts';
import { formatDate } from '../../../utils/entityUtils';

interface CommandeContentProps {
  commande: CommandeBC;
  activeTab: TabDescriptor | null;
  // Tous les onglets : sert a decider si une destination de bascule est ouverte (un CTA de
  // navigation ne s'affiche que si `goToTab` y menerait vraiment).
  descriptors: TabDescriptor[];
  // Rafraichit la commande apres une action (remplace window.location.reload()).
  refetch: () => Promise<void> | void;
  // Bascule vers un onglet par son `kind` (ex. renvoyer vers Offre depuis Consultation).
  goToTab?: (kind: TabDescriptor['kind']) => void;
  // Destination declaree (liste de preferences), arbitree au render suivant. Necessaire apres une
  // signature : `goToTab` juge la destination sur les descripteurs capturees AVANT le refetch, donc
  // il voit ferme un onglet que la signature vient d'ouvrir.
  requestTab?: (preferences: string[]) => void;
  // CGV de session (remontees au shell) : passees a l'onglet Offre pour l'acceptation signee.
  acceptCGV?: boolean;
  setAcceptCGV?: React.Dispatch<React.SetStateAction<boolean>>;
  // Vide le badge messagerie de l'onglet apres le mark-read serveur (declenche par TabMessagerie).
  onMarkRead?: (channel: MessageChannel) => void;
}

// Routeur d'onglet par `kind` (jamais par index). Les contenus arrivent au fil des etapes :
//   - historique   -> CommandeSuiviMarche (etape 3, fait)
//   - consultation -> CommandeConsultation (etape 4, fait)
//   - offre        -> CommandeOffre (etape 5, fait)
//   - notification -> CommandeNotification (bon de commande signe, actif au statut Valide = 7)
//   - messagerie   -> TabMessagerie (canal prive, actif quand la commande est validee = 7)
//   - evaluation   -> CommandeEvaluationTab (lecture seule cote presta)
const CommandeContent: React.FC<CommandeContentProps> = ({
  commande,
  activeTab,
  descriptors,
  refetch,
  goToTab,
  requestTab,
  acceptCGV,
  setAcceptCGV,
  onMarkRead,
}) => {
  if (!activeTab) return null;

  // Une bascule n'est proposee que si `goToTab` peut aboutir : la destination doit exister ET etre
  // ouverte. Sans ca le bouton serait un clic mort (goToTab ignore les onglets grises).
  const goTo = (kind: TabDescriptor['kind']): (() => void) | undefined =>
    goToTab && isTabReachable(descriptors, kind) ? () => goToTab(kind) : undefined;

  switch (activeTab.kind) {
    case 'historique':
      return <CommandeSuiviMarche commande={commande} />;
    case 'consultation':
      return (
        <CommandeConsultation commande={commande} refetch={refetch} onGoToOffre={goTo('offre')} />
      );
    case 'offre':
      return (
        <CommandeOffre
          commande={commande}
          refetch={refetch}
          acceptCGV={acceptCGV}
          setAcceptCGV={setAcceptCGV}
          onGoToNotification={goTo('notification')}
          requestTab={requestTab}
        />
      );
    case 'notification':
      // Actif uniquement au statut Valide (7), gate en domaine (commandeTabs).
      return <CommandeNotification commande={commande} onGoToMessagerie={goTo('messagerie')} />;
    case 'messagerie': {
      // Le CTA vit dans la barre d'envoi de la messagerie : c'est le composant partage qui la rend,
      // donc il le recoit deja construit (cf. prop `nextTabButton`).
      const messagerieCta = computeMessagerieNextTabCta(isTabReachable(descriptors, 'evaluation'));
      const goToEvaluation = goTo('evaluation');
      // Bandeau vert "messagerie active" (parite MS) : commande signee/acceptee au statut Valide (7).
      // Date d'activation = signature de la commande (updated_at).
      const marcheSigne = (commande.status?.id ?? commande.id_status) === StatusId.Valide;
      const messagerieActiveSince = marcheSigne
        ? commande.updated_at
          ? formatDate(commande.updated_at)
          : null
        : undefined;
      return (
        <TabMessagerie
          operationId={commande.operation?.id ?? 0}
          operationStatus={commande.status?.id ?? commande.id_status ?? 0}
          channel="messagerie"
          apiBase="/bdc"
          onMarkRead={onMarkRead}
          messagerieActiveSince={messagerieActiveSince}
          nextTabButton={
            <NextTabButton
              label={messagerieCta.label}
              hidden={messagerieCta.hidden}
              onClick={() => goToEvaluation?.()}
            />
          }
        />
      );
    }
    case 'evaluation':
      // Cote prestataire : lecture seule (l'acheteur depose l'avis depuis sa vue).
      return (
        <>
          <CommandeEvaluationTab
            typeCommande="BC"
            commandeId={commande.id}
            validated={(commande.status?.id ?? commande.id_status) === StatusId.Valide}
          />
          <CommandeEvaluationTab
            typeCommande="BC"
            commandeId={commande.id}
            validated={(commande.status?.id ?? commande.id_status) === StatusId.Valide}
            cible="acheteur"
          />
        </>
      );
    default:
      return null;
  }
};

export default CommandeContent;
